import { useSearchParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { DollarCircle, FilterList, NavArrowDown } from 'iconoir-react'
import classNames from 'classnames'
import { compact, uniqBy } from 'lodash'
import { CSSProperties, useEffect, useMemo, useState } from 'react'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import LocationSelector from '/app/components/analytics/LocationSelector'
import LocationCoverageNotice from 'app/components/analytics/LocationCoverageNotice'
import { ChannelSelector } from '../marketing/ChannelSelector'
import MarketingDateRangeSelector, {
  DateRangeCompareOptions,
  DateRangeOptions,
} from '../marketing/MarketingDateRangeSelector'
import { PageHeader } from '../PageHeader'
import { CampaignComboOption } from '../table/filters/CampaignComboOption'
import { TableFiltersView as TableFilters } from 'app/components/ui/table-filters/table-filters'
import { Feature } from 'app/components/auth/Feature'
import { Button } from 'app/components/ui/button'
import { Toggle } from 'app/components/Toggle'
import { Tooltip } from 'app/components/Tooltip'
import { FilterValueType, FilterableColumn, decodeFilters } from 'app/filter.utils'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import type { CampaignJson } from 'app/types/entities'
import type { LocationJson, LocationProductType } from 'app/types/entities'
import { SearchResponse } from 'app/services/search'
import { hasRole, useUser, useWorkspace } from 'app/utils'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { useLocationIntegrationStatus } from 'app/hooks/useLocationIntegrationStatus'
import { isSMCOrg } from 'lib/isSMCOrg'
import { debug } from 'lib/logging'
import { Jsonify } from 'lib/toJSON'
import { OptionType } from 'types/OptionType'
import { MONEY_ICON_FILL } from 'app/lib/iconColors'

export const CUSTOM_FILTERS_SEARCH_PARAM = `dashboardFilters`

const allLocationsOption = { name: `All Locations`, id: `0`, value: `` }
const allIntegratedLocationsOption = { name: `All PMS Integrated`, id: `1`, value: `integrated` }
const allNonIntegratedLocationsOption = { name: `All Non-PMS Integrated`, id: `2`, value: `notIntegrated` }
/**
 * Filters query attributes of a model, however dashboard does not necessary have a specific model.
 * Selected filters should be transformed to something that can be used to query the metrics
 * @param addMarketingFilters Flag to define if marketing filters should be added
 * @param defaultChannel Value use for channel. Eg.`Digital Marketing`
 * @param type Facebook or Google
 * @param showUtmFields flag to show UTM fields (source, medium, campaign)
 * @returns FilterableColumn[]
 */
const getFilterableColumns: (
  addMarketingFilters: boolean,
  type: string,
  defaultChannel?: string | null,
  showUtmFields?: boolean,
  hideAdFilters?: boolean,
  channelFetcher?: FilterableColumn[`fetcher`],
) => FilterableColumn[] = (addMarketingFilters, type, defaultChannel, showUtmFields, hideAdFilters, channelFetcher) => {
  return compact([
    {
      attribute: `channel.keyword`,
      displayName: `Channel`,
      type: FilterValueType.asyncMultiSelect,
      customSearchParam: `channel`,
      defaultValue: defaultChannel != null && defaultChannel !== `` ? defaultChannel : `*`,
      defaultFilter: !!defaultChannel ? true : false,
      defaultOperator: `exactly`,
      optionsSource: channelFetcher ? undefined : `/api/channels`,
      fetcher: channelFetcher,
    },
    ...(showUtmFields
      ? [
          {
            attribute: `utmSource`,
            displayName: `Source`,
            type: FilterValueType.string,
          } as FilterableColumn,
          {
            attribute: `utmMedium`,
            displayName: `Medium`,
            type: FilterValueType.string,
          } as FilterableColumn,
          {
            attribute: `utmCampaign`,
            displayName: `Campaign`,
            type: FilterValueType.string,
          } as FilterableColumn,
        ]
      : []),
    ...(addMarketingFilters && !hideAdFilters
      ? [
          {
            attribute: `platform`,
            displayName: `Ad Platform`,
            customSearchParam: `platform`,
            defaultOperator: `exactly`,
            defaultValue: [`*`],
            useAsterisk: true,
            customComboOption: () => (
              <div className="flex items-center">
                <DollarCircle
                  height={16}
                  width={16}
                  className="mr-1 fill-(--money-icon-fill) text-on-color"
                  style={{ '--money-icon-fill': MONEY_ICON_FILL } as CSSProperties}
                />
                <span>Ad Platform</span>
              </div>
            ),
            type: FilterValueType.multiple,
            options: [
              {
                id: `facebook`,
                name: `Facebook Ads`,
                value: `Facebook Ads`,
                meta: { icon: `/public/meta_icon.png` },
              },
              {
                id: `google`,
                name: `Google Ads`,
                value: `Google Ads`,
                meta: { icon: `/public/google_ads.png` },
              },
              {
                id: `local-service-ads`,
                name: `Local Service Ads`,
                value: `Local Service Ads`,
                meta: { icon: `/public/google_ads.png` },
              },
            ],
          } as FilterableColumn,
          {
            attribute: `trackableCampaignId`,
            displayName: `Ad Campaign`,
            // contextualFiltering: { trackableCampaignId: `accountId` }, // utmAccountId is used to query contacts, accountId is used to query campaigns
            type: FilterValueType.id,
            defaultValue: [`*`],
            nonEmpty: true,
            customComboOption: () => (
              <div className="flex items-center">
                <DollarCircle
                  height={16}
                  width={16}
                  className="mr-1 fill-(--money-icon-fill) text-on-color"
                  style={{ '--money-icon-fill': MONEY_ICON_FILL } as CSSProperties}
                />
                <span>Ad Campaign</span>
              </div>
            ),
            optionsSource: `/api/campaigns`,
            additionalParams: { type },
            optionsMapper: (res: Jsonify<SearchResponse<CampaignJson>>) => {
              if (res.total > 0) {
                return uniqBy(
                  res.hits
                    ?.sort((a, b) => {
                      if (a.type && b.type) return a.type?.localeCompare(b?.type)
                      return -1
                    })
                    ?.map(
                      (entry) =>
                        ({
                          id: entry.id,
                          value: entry.id,
                          name: entry.name,
                          meta: {
                            customComboOption: ({ selected }) => (
                              <CampaignComboOption
                                campaign={entry}
                                workspaceId={entry.workspaceId}
                                selected={selected}
                                type={entry.type!}
                              />
                            ),
                          },
                        }) as OptionType,
                    ),
                  `value`,
                )
              }
              return []
            },
          } as FilterableColumn,
        ]
      : []),
  ])
}

// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
const DashboardHeader = observer(function DashboardHeader({
  title,
  defaultChannel,
  dateChangeHandler,
  limitDateRangeToMonth,
  hideCompare,
  defaultDateRange,
  allowFutureDates,
  compareSelectorBlacklist,
  defaultCompareSelectorPreset,
  hideChannelSelector,
  hideLocationSelector,
  hideGroupedLocationOptions,
  locationProduct,
  requireProductInUse,
  hideUniqueNewPatientsToggle,
  onFiltersChange,
  extraButtons,
  hideAdFilters,
  channelFetcher,
  channelScopeKey,
  noticeClassName,
}: {
  title: string
  defaultChannel?: string | null
  dateChangeHandler?: (
    fromDate: string,
    toDate: string,
    compareFromDate: string,
    compareToDate: string,
    comparingGoals: boolean,
    dontCompare: boolean,
  ) => void
  limitDateRangeToMonth?: boolean
  hideCompare?: boolean
  defaultDateRange?: DateRangeOptions
  defaultCompareSelectorPreset?: DateRangeCompareOptions
  allowFutureDates?: boolean
  compareSelectorBlacklist?: DateRangeCompareOptions[]
  hideChannelSelector?: boolean
  hideLocationSelector?: boolean
  /**
   * Only expose concrete location IDs. Use this when a dashboard query does not
   * support DashboardHeader's aggregate `integrationStatus` filter.
   */
  hideGroupedLocationOptions?: boolean
  /**
   * Narrows the location selector to locations entitled to this product. Use on
   * product-specific dashboards (e.g. AI Voice Agent) so the list can't offer
   * locations the dashboard has no data for.
   */
  locationProduct?: LocationProductType | LocationProductType[]
  /**
   * Narrows further to locations where that product is actually in use: switched on now
   * (the AI Agent / Scheduler "live" toggle), or already responsible for data there.
   * Entitlement alone is set when the product is sold, so on its own it still offers
   * locations the agent never answered for.
   */
  requireProductInUse?: boolean
  hideUniqueNewPatientsToggle?: boolean
  integrationWarningSpacing?: boolean
  /** Spacing for the location coverage notice, for a page laid out differently. */
  noticeClassName?: string
  onFiltersChange?: (filters: string) => void
  extraButtons?: React.ReactNode
  hideAdFilters?: boolean
  channelFetcher?: FilterableColumn[`fetcher`]
  channelScopeKey?: string
}) {
  const me = useUser()
  const workspace = useWorkspace()
  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const checkPermission = useCheckPermission()
  const isMobile = useIsMobile()
  // Collapsed controls stay mounted (hidden via CSS, not unmounted) so they keep
  // driving data loads while the mobile panel is closed.
  const [filtersOpen, setFiltersOpen] = useState(false)

  const isSMC = me ? isSMCOrg(me.orgId!) : false

  // Legacy role-based logic (staff-v2 OFF)
  const legacyAddMarketingFilters = me
    ? me.type === `super` || (isSMC && hasRole(me, `orgAdmin`)) || (!isSMC && !hasRole(me, `orgAssistant`))
    : false
  const legacyShowUtmFields = !isSMC

  // staff-v2 ON: SMC needs advanced-filters permission for marketing columns, non-SMC always gets them
  // staff-v2 OFF: use legacy role-based logic
  const addMarketingFilters = staffV2Enabled
    ? isSMC
      ? checkPermission(`org:marketing:advanced-filters`)
      : true
    : legacyAddMarketingFilters
  const useTableFilters = staffV2Enabled ? (isSMC ? addMarketingFilters : true) : legacyAddMarketingFilters
  const showUtmFields = staffV2Enabled ? (isSMC ? checkPermission(`org:contacts:see-utm`) : true) : legacyShowUtmFields

  const [searchParams, setSearchParams] = useSearchParams()
  const locationIdsParam = searchParams.get(`locationIds`)
  const locationIds = useMemo(() => compact(locationIdsParam?.split(`,`) ?? []), [locationIdsParam])
  const { locationOptions } = useLocationIntegrationStatus([locationIds], locationProduct, requireProductInUse)
  // Demo workspaces are intentionally partial, so their coverage gaps are not news.
  const showIntegrationWarning = !workspace || !workspace.id?.match(/demo/i)
  const [hideMultiLocationPatients, setHideMultiLocationPatients] = useState(
    searchParams.get(`hideMultiLocationPatients`) === `true`,
  )

  const deselectAllOptions = useMemo(() => {
    if (hideGroupedLocationOptions) return [allLocationsOption]
    if (
      locationOptions.every(
        (option) => option.meta?.integrationStatus === `integrated` || option.meta?.integrationStatus === `missingData`,
      )
    ) {
      return [allLocationsOption]
    }
    if (locationOptions.every((option) => option.meta?.integrationStatus === `notIntegrated`)) {
      return [allLocationsOption]
    }
    return [allLocationsOption, allIntegratedLocationsOption, allNonIntegratedLocationsOption]
  }, [hideGroupedLocationOptions, locationOptions])

  const customFiltersParam = searchParams.get(CUSTOM_FILTERS_SEARCH_PARAM)
  const integrationStatusParam = searchParams.get(`integrationStatus`)
  const filterableColumns = useMemo(
    () =>
      getFilterableColumns(
        addMarketingFilters,
        (decodeFilters(customFiltersParam).find((filter) => filter.attribute === `platform`)?.value as string) ?? ``,
        searchParams.has(`channel`) ? searchParams.get(`channel`)! : defaultChannel,
        showUtmFields,
        hideAdFilters,
        channelFetcher,
      ),
    [
      defaultChannel,
      locationIds,
      customFiltersParam,
      me,
      showUtmFields,
      addMarketingFilters,
      hideAdFilters,
      channelFetcher,
    ],
  )
  // useMemo needed so we don't retrigger the integration warning on every render
  const selectedOptions = useMemo(() => {
    const selectedLocationOptions = locationOptions.filter((option) => locationIds.includes(option.value))
    if (!hideGroupedLocationOptions && selectedLocationOptions.length === 0 && !!integrationStatusParam) {
      return integrationStatusParam === `integrated`
        ? [allIntegratedLocationsOption]
        : [allNonIntegratedLocationsOption]
    }
    return selectedLocationOptions
  }, [hideGroupedLocationOptions, locationOptions, locationIds, integrationStatusParam])

  useEffect(() => {
    if (!hideGroupedLocationOptions || !searchParams.has(`integrationStatus`)) return
    setSearchParams(
      (prev) => {
        prev.delete(`integrationStatus`)
        return prev
      },
      { replace: true },
    )
  }, [hideGroupedLocationOptions, integrationStatusParam])

  const onLocationChange = (options: OptionType[]) => {
    if (options.length === 0) {
      setSearchParams((prev) => {
        prev.delete(`locationIds`)
        prev.delete(`integrationStatus`)
        return prev
      })
    }
    switch (options[0]?.value) {
      case allLocationsOption.value: // All Locations selected
        setSearchParams((prev) => {
          prev.delete(`locationIds`)
          prev.delete(`integrationStatus`)
          return prev
        })
        break
      case allIntegratedLocationsOption.value:
        setSearchParams((prev) => {
          prev.delete(`locationIds`)
          prev.set(`integrationStatus`, `integrated`)
          return prev
        })
        break
      case allNonIntegratedLocationsOption.value:
        setSearchParams((prev) => {
          prev.delete(`locationIds`)
          prev.set(`integrationStatus`, `notIntegrated`)
          return prev
        })
        break
      default:
        if (options.length > 0) {
          setSearchParams((prev) => {
            prev.set(`locationIds`, compact(options?.map((option) => option.value!)).join(`,`))
            prev.set(`integrationStatus`, ``)
            return prev
          })
        }
        break
    }
  }

  useEffect(() => {
    debug(`multi-location-patients`, `hideMultiLocationPatients`, hideMultiLocationPatients)
    setSearchParams((prev) => {
      if (hideMultiLocationPatients) {
        prev.set(`hideMultiLocationPatients`, `true`)
      } else {
        prev.delete(`hideMultiLocationPatients`)
      }
      return prev
    })
  }, [hideMultiLocationPatients])

  if (!me) return null

  // Named so mobile can regroup them into fewer rows without changing desktop.
  const disqualifiedToggle = extraButtons ? <>{extraButtons}</> : null
  const uniqueToggle = !hideUniqueNewPatientsToggle && (
    <Feature name="TRK-2637">
      <Tooltip
        className="max-w-75"
        placement="bottom"
        message="Only count unique new patient records regardless of location when calculating analytics. This may cause a decrease in other metrics like LTV and should only be used when analyzing new patient metrics."
      >
        <Toggle
          label={isMobile ? `Unique Patients` : `Only Unique New Patients`}
          showLabelOnLeft={true}
          defaultValue={hideMultiLocationPatients}
          handleToggle={setHideMultiLocationPatients}
        />
      </Tooltip>
    </Feature>
  )
  const channelControl = !hideChannelSelector && !useTableFilters && (
    <ChannelSelector key={channelScopeKey} defaultChannel={defaultChannel} fetcher={channelFetcher} />
  )
  const filtersControl = !hideChannelSelector && useTableFilters && (
    <TableFilters
      key={channelScopeKey}
      columns={filterableColumns}
      customSearchParam={CUSTOM_FILTERS_SEARCH_PARAM}
      useOrOption={false}
      onFiltersChange={(filters: string) => onFiltersChange?.(filters)}
    />
  )
  const locationControl = !hideLocationSelector && (
    <LocationSelector
      selectedOptions={selectedOptions}
      locationOptions={locationOptions}
      deselectAllOptions={deselectAllOptions}
      onChange={onLocationChange}
      hideLabel={isMobile}
    />
  )
  const dateControl = (
    <MarketingDateRangeSelector
      onDateChange={dateChangeHandler}
      limitToMonth={limitDateRangeToMonth}
      hideCompare={hideCompare}
      defaultSelectorPreset={defaultDateRange}
      defaultCompareSelectorPreset={defaultCompareSelectorPreset}
      allowFutureDates={allowFutureDates}
      compareSelectorBlacklist={compareSelectorBlacklist}
      customLabelClassName="ml-2 text-ui-xs text-default"
    />
  )

  // Desktop: the original single right-aligned cluster (order unchanged).
  const desktopControls = (
    <>
      {disqualifiedToggle}
      {uniqueToggle}
      {channelControl}
      {filtersControl}
      {locationControl}
      {dateControl}
    </>
  )

  const hasToggles = Boolean(disqualifiedToggle) || Boolean(uniqueToggle)
  const mobileControls = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {channelControl}
        {filtersControl}
        {locationControl}
      </div>
      {hasToggles && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          {disqualifiedToggle}
          {uniqueToggle}
        </div>
      )}
    </>
  )

  return (
    <div className="flex flex-wrap items-center justify-between align-middle">
      <PageHeader title={title} className="pb-6" />
      {/* `w-full` is what drops this row below the title rather than sharing its line. */}
      {isMobile && (
        <div className="mb-6 flex w-full flex-wrap items-center gap-2">
          {dateControl}
          <Button variant="tertiary" onClick={() => setFiltersOpen((open) => !open)} aria-expanded={filtersOpen}>
            <FilterList className="h-4 w-4" strokeWidth={2} />
            Filters
            <NavArrowDown
              className={classNames(`h-4 w-4 text-minimal transition-transform`, filtersOpen && `rotate-180`)}
              strokeWidth={2}
            />
          </Button>
        </div>
      )}
      {/* Desktop: the inline right-aligned cluster. Mobile: a full-width column
          that collapses to `hidden` behind the Filters button above. */}
      <div
        className={classNames(
          isMobile
            ? [
                // Contained panel so the controls read as a grouped dropdown
                // rather than floating on the page.
                `mb-6 w-full flex-col gap-4 rounded-lg border border-default bg-surface p-4 shadow-elevation-2`,
                filtersOpen ? `flex` : `hidden`,
              ]
            : `flex flex-wrap items-center justify-end gap-x-4 gap-y-2 pb-6`,
        )}
      >
        {isMobile ? mobileControls : desktopControls}
      </div>
      {showIntegrationWarning && (
        <LocationCoverageNotice
          selectedOptions={selectedOptions}
          locationOptions={locationOptions}
          className={noticeClassName}
        />
      )}
    </div>
  )
})

export default DashboardHeader
